import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  ActivityIcon,
  CircleDotIcon,
  ClockIcon,
  MapPinIcon,
  PackageCheckIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ARC_COLORS, StatusCategory } from "../map-statuses";
import { MapThemePalette, rgbaCss } from "@/components/deck-map/_helpers/map-theme";

interface MapLegendProps {
  activeCount: number;
  pendingCount: number;
  arrivingCount: number;
  issueCount: number;
  branchCount: number;
  totalOrders: number;
  palette: MapThemePalette;
}

const LEGEND_ITEMS: ReadonlyArray<{
  key: StatusCategory;
  icon: LucideIcon;
  labelKey: string;
}> = [
  { key: StatusCategory.Active, icon: ActivityIcon, labelKey: "MapLegendActive" },
  { key: StatusCategory.Pending, icon: ClockIcon, labelKey: "MapLegendPending" },
  { key: StatusCategory.Arriving, icon: PackageCheckIcon, labelKey: "MapLegendArriving" },
  { key: StatusCategory.Issue, icon: TriangleAlertIcon, labelKey: "MapLegendIssue" },
];

export function MapLegend({
  activeCount,
  pendingCount,
  arrivingCount,
  issueCount,
  branchCount,
  totalOrders,
  palette,
}: MapLegendProps) {
  const { t } = useTranslation(["fit"]);

  const counts: Record<StatusCategory, number> = {
    [StatusCategory.Active]: activeCount,
    [StatusCategory.Pending]: pendingCount,
    [StatusCategory.Arriving]: arrivingCount,
    [StatusCategory.Issue]: issueCount,
  };

  return (
    <Card
      className="w-64 backdrop-blur-md"
      style={{
        backgroundColor: palette.surface.panelBackground,
        borderColor: palette.surface.border,
        color: palette.text.primary,
        boxShadow: palette.surface.shadow,
      }}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <MapPinIcon className="h-4 w-4" style={{ color: palette.icon.accent }} />
          {t("fit:TransitMapLegend")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {LEGEND_ITEMS.map(({ key, icon: Icon, labelKey }) => (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: rgbaCss(ARC_COLORS[key], palette.mode === "dark" ? 1 : 0.95) }}
                />
                <Icon className="h-3.5 w-3.5" style={{ color: palette.icon.muted }} />
                <span className="text-xs" style={{ color: palette.text.secondary }}>
                  {t(`fit:${labelKey}`)}
                </span>
              </div>
              <span className="text-xs font-medium tabular-nums">{counts[key]}</span>
            </div>
          ))}
        </div>

        <Separator style={{ backgroundColor: palette.surface.separator }} />

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <CircleDotIcon className="h-3.5 w-3.5" style={{ color: palette.icon.muted }} />
              <span style={{ color: palette.text.secondary }}>{t("fit:MapLegendBranches")}</span>
            </div>
            <span className="font-medium tabular-nums">{branchCount}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <ActivityIcon className="h-3.5 w-3.5" style={{ color: palette.icon.muted }} />
              <span style={{ color: palette.text.secondary }}>{t("fit:MapLegendTotalOrders")}</span>
            </div>
            <span className="font-medium tabular-nums">{totalOrders}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
